import React, { useEffect, useRef, useState } from 'react'
import { List, Skeleton, Empty, Spin } from 'antd'
import { formatDistanceToNow } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { NotificationEntity } from '@/store/notification'
import { NotificationStatus, NotificationType } from '@shared/constants/notification'
import throttle from 'lodash/throttle'
import { BellOutlined, BugOutlined, SmileOutlined, AlertOutlined, NotificationOutlined } from '@ant-design/icons'

export const createMarkup = (html: string) => {
  return {
    __html: html
  };
};

interface NotificationListProps {
  notifications: NotificationEntity[]
  loading: boolean
  onItemClick?: (item: NotificationEntity) => void
  onLoadMore?: () => Promise<boolean> // 返回是否还有更多数据
  hasMore?: boolean // 是否还有更多数据
}

const NotificationList: React.FC<NotificationListProps> = ({ 
  notifications, 
  loading, 
  onItemClick,
  onLoadMore,
  hasMore = false
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const [loadingMore, setLoadingMore] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // 格式化时间
  const formatTime = (time: string | Date) => {
    const date = typeof time === 'string' ? new Date(time) : time
    return formatDistanceToNow(date, { addSuffix: true, locale: zhCN })
  }

  // 处理点击消息项
  const handleItemClick = (item: NotificationEntity) => {
    if (onItemClick) {
      onItemClick(item)
    }
  }

  // 处理滚动加载
  const handleScroll = async () => {
    const container = scrollContainerRef.current
    console.log('滚动事件触发')
    // console.log(container,loadMoreElement,hasMore,onLoadMore )
    if (!container || loadingMore || !hasMore || !onLoadMore) return
    const { scrollTop, scrollHeight, clientHeight } = container
    // console.log('xx', scrollHeight - scrollTop - clientHeight < 100)

    // 当滚动到距离底部100px时加载更多
    if (scrollHeight - scrollTop - clientHeight < 100) {
      setLoadingMore(true)
      try {
        console.log('触发加载更多') // 添加日志确认函数被调用
        const hasMoreData = await onLoadMore()
        console.log('加载更多结果:', hasMoreData) // 添加日志查看返回结果
      } finally {
        setLoadingMore(false)
      }
    }
  }

  // 添加滚动事件监听
  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      // 使用节流函数优化滚动事件
      const throttledScroll = throttle(handleScroll, 300)
      container.addEventListener('scroll', throttledScroll)
      
      return () => {
        container.removeEventListener('scroll', throttledScroll)
      }
    }
  }, [loadingMore, hasMore, onLoadMore])

  useEffect(() => {
    if (notifications.length === 0 && !loading && hasMore && onLoadMore) {
      onLoadMore()
    }
  }, [])

  const loadMoreElement = hasMore ? (
    <div className="notification-load-more">
      {loadingMore && <Spin size="small" />}
    </div>
  ) : null

  // 根据消息类型获取对应的图标
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.SYSTEM:
        return <NotificationOutlined className="notification-icon system" />;
      case NotificationType.FEEDBACK:
        return <SmileOutlined className="notification-icon feedback" />;
      case NotificationType.ANNOUNCEMENT:
        return <AlertOutlined className="notification-icon announcement" />;
      default:
        return <BellOutlined className="notification-icon default" />;
    }
  };

  // 处理展开/收起
  const toggleExpand = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // 处理消息内容
  const processContent = (item: NotificationEntity) => {
    if (!item.content) return '';
    let content = item.content;
    
    // 替换链接占位符
    if (item.linkUrl && content.includes('{link_url}')) {
      content = content.replace('{link_url}', item.linkUrl);
    }
    
    return <div dangerouslySetInnerHTML={createMarkup(content)} />;
  };

  return (
    <div 
      className="notification-list" 
      ref={scrollContainerRef}
      style={{ maxHeight: '350px', overflowY: 'auto' }}
    >
      <Skeleton loading={loading && notifications.length === 0} active paragraph={{ rows: 3 }}>
        {notifications.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            loadMore={loadMoreElement}
            split={true}
            renderItem={(item) => (
              <List.Item 
                onClick={() => handleItemClick(item)}
                className={`notification-item ${item.status === NotificationStatus.UNREAD ? 'unread' : ''}`}
              >
                <List.Item.Meta
                  avatar={getNotificationIcon(item.type)}
                  title={item.title}
                  description={
                    <div>
                      <div className={`notification-content ${!expandedItems.has(item.id) ? 'truncated' : ''}`}>
                        {processContent(item)}
                      </div>
                      {item.content.length > 100 && (
                        <a 
                          className="expand-link"
                          onClick={(e) => toggleExpand(e, item.id)}
                        >
                          {expandedItems.has(item.id) ? '收起' : '查看更多'}
                        </a>
                      )}
                      <div className="notification-time">{formatTime(item.createdAt)}</div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description="暂无消息" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Skeleton>
    </div>
  )
}

export default NotificationList